import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { showsAPI } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { useShowStore } from '../../stores/showStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { useClockTick } from '../../contexts/ClockTickContext.jsx';
import { exportCuesAsCsv } from '../../lib/rundownCsv';
import { extractShowStartHMS, hmsToScheduledStart } from '../../lib/showTime';
import EditLockButton from './EditLockButton';

const VIEWS = [
  { id: 'producer', label: 'Producer', key: '1' },
  { id: 'td', label: 'TD', key: '2' },
  { id: 'graphics', label: 'Graphics', key: '3' },
  { id: 'commentary', label: 'Commentary', key: '4' },
  { id: 'raw', label: 'Raw', key: '5' },
  { id: 'singular', label: 'Singular', key: '6' },
];

const STATUS_PILL = {
  draft:    { label: 'DRAFT',    color: 'var(--faint)' },
  ready:    { label: 'READY',    color: 'var(--blue)' },
  live:     { label: 'LIVE',     color: 'var(--live)' },
  complete: { label: 'COMPLETE', color: 'var(--green)' },
};

function pad2(n) { return String(n).padStart(2, '0'); }

// Show-start is a timezone-independent wall-clock — read via the shared helper
// (UTC basis) so it matches what was typed on any machine. See lib/showTime.js.
const extractHMS = extractShowStartHMS;

// ── inline icons (project ships no icon library) ──────────────────────────────
const svgBase = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', viewBox: '0 0 24 24' };
function IconChevronLeft(p) { return <svg {...svgBase} {...p}><path d="M15 19l-7-7 7-7" /></svg>; }
function IconMenu2(p) { return <svg {...svgBase} {...p}><path d="M4 6h16M4 12h16M4 18h16" /></svg>; }
function IconAlert(p) { return <svg {...svgBase} {...p}><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>; }

// ── §2 clock stack: caption directly above a right-aligned value ──────────────
function ClockStack({ caption, value, valueColor, valuePx }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <span style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--dim)', lineHeight: '10px' }}>
        {caption}
      </span>
      <span style={{
        fontFamily: "'Roboto Mono','Roboto Mono Fallback',monospace", fontVariantNumeric: 'tabular-nums',
        fontSize: valuePx, fontWeight: 400, lineHeight: `${valuePx}px`, color: valueColor,
      }}>
        {value}
      </span>
    </div>
  );
}

// ── §3 connection chip: three peripherally-distinct treatments ────────────────
// connected → filled green dot · connecting → amber dot slow-pulse ·
// disconnected → whole chip flips to --over (bg + label) with an alert icon.
function ConnChip({ state, label, tooltip, pulseKey }) {
  const down = state === 'disconnected';
  const connecting = state === 'connecting';
  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 5,
        background: down ? 'color-mix(in srgb, var(--over) 12%, transparent)' : 'transparent',
        whiteSpace: 'nowrap',
      }}
    >
      {down ? (
        <IconAlert width={12} height={12} style={{ color: 'var(--over)' }} />
      ) : (
        <span
          key={pulseKey}
          className={connecting ? 'mc-slow-pulse' : (pulseKey ? 'mc-tally-pulse' : undefined)}
          style={{ width: 7, height: 7, borderRadius: '50%', background: connecting ? 'var(--next)' : 'var(--green)' }}
        />
      )}
      <span style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.05em', color: down ? 'var(--over)' : 'var(--dim)' }}>
        {label}
      </span>
    </span>
  );
}

// ── §5 tier-3 mode chip (on/off state via role colour) ────────────────────────
function ModeChip({ active, tone, title, onClick, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        padding: '5px 9px', borderRadius: 5, fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em',
        color: active ? `var(${tone})` : 'var(--faint)',
        border: `1px solid ${active ? `var(${tone})` : 'var(--border)'}`,
        background: active ? `color-mix(in srgb, var(${tone}) 12%, transparent)` : 'transparent',
        cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >{children}</button>
  );
}

export default function TopBar({
  show, showId, activeView, onViewChange, onExit, onOpenSettings,
  onTake, onSkip, onBack, onReset,
  canTake, autoMode, onToggleAutoMode, takeGuard, onToggleTakeGuard,
}) {
  const navigate = useNavigate();
  useClockTick(); // 1s re-render for TIME + SEG

  const { user, logout, getSubscriptionStatus, getTrialDaysRemaining } = useAuthStore();
  // Renaming, the show start time and show settings are production edits; a
  // text-only guest still gets the transport, GO LIVE, END and RESET.
  const canProduce = !!user?.capabilities?.includes('production:edit');
  const { setShow } = useShowStore.getState();
  const onAirCue = useShowStore(s => s.cues.find(c => c.status === 'on_air') || null);
  const onAirStartTime = useShowStore(s => s.onAirStartTime);

  const followOnAir = useSettingsStore(s => s.followOnAir);
  const toggleFollowOnAir = useSettingsStore(s => s.toggleFollowOnAir);

  const wsConnected = useConnectionStore(s => s.wsConnected);
  const wsReconnecting = useConnectionStore(s => s.wsReconnecting);
  const vmixConnected = useConnectionStore(s => s.vmixConnected);
  const vmixTally = useConnectionStore(s => s.vmixTally, shallow);
  const singularConnected = useConnectionStore(s => s.singularConnected);
  const singularConfigured = useConnectionStore(s => s.singularConfigured);

  const [menuOpen, setMenuOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [editingStart, setEditingStart] = useState(false);
  const [startDraft, setStartDraft] = useState('');
  const [tallyPulse, setTallyPulse] = useState(0);
  const menuRef = useRef(null);
  const tallySigRef = useRef('');

  const isLive = show?.status === 'live';
  const isDraft = show?.status === 'draft' || show?.status === 'ready';
  const statusInfo = STATUS_PILL[show?.status] || STATUS_PILL.draft;

  // §3 hide-if-unused: only render an indicator for a service this rundown uses.
  // SYNC (our own websocket) is always in use. vMix/Singular depend on config.
  const vmixInUse = !!show?.vmix_host;
  // show.singular_api_key is redacted over REST and absent under local-first, so
  // the stored-token fact comes from the server probe held in the store.
  const singularInUse = singularConfigured || !!show?.singular_output_id;

  const syncState = wsConnected ? 'connected' : (wsReconnecting ? 'connecting' : 'disconnected');
  const vmixState = vmixConnected ? 'connected' : 'disconnected';
  const singularState = singularConnected ? 'connected' : 'disconnected';

  // Pulse the vMix dot whenever a tally change lands — proves data is flowing,
  // not just that a socket is open (a green dot alone can't).
  useEffect(() => {
    const sig = JSON.stringify([vmixTally?.program || [], vmixTally?.preview || []]);
    if (tallySigRef.current && tallySigRef.current !== sig) setTallyPulse(p => p + 1);
    tallySigRef.current = sig;
  }, [vmixTally]);

  const vmixTooltip = `vMix — PGM ${(vmixTally?.program?.length ? vmixTally.program.join(', ') : '—')} · PRV ${(vmixTally?.preview?.length ? vmixTally.preview.join(', ') : '—')}`;

  // Close hamburger on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  useEffect(() => {
    if (!editingStart) setStartDraft(extractHMS(show?.scheduled_start) || '');
  }, [show?.scheduled_start, editingStart]);

  const subscriptionStatus = getSubscriptionStatus();
  const trialDaysRemaining = getTrialDaysRemaining();

  async function handleStartShow() {
    if (!confirm('Start show? This will set the show to LIVE and begin the clock.')) return;
    try { await showsAPI.start(showId); } catch (err) { console.error('Failed to start show:', err); }
  }
  async function handleEndShow() {
    if (!confirm('End show? This will mark the show as complete.')) return;
    try { await showsAPI.end(showId); } catch (err) { console.error('Failed to end show:', err); }
  }
  async function handleResetShow() {
    setMenuOpen(false);
    if (!confirm('Reset show? All cue statuses will be cleared and the show will return to Draft.')) return;
    try { await showsAPI.reset(showId); onReset?.(); } catch (err) { console.error('Failed to reset show:', err); }
  }
  function handleDownload() { setMenuOpen(false); exportCuesAsCsv(show, useShowStore.getState().cues); }
  function handlePrint() { setMenuOpen(false); if (show?.id) window.open(`/shows/${show.id}/print`, '_blank'); }
  function handleHelp() { setMenuOpen(false); window.open('/help', '_blank'); }

  async function handleNameSave() {
    setEditingName(false);
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === show?.name || !show?.id) return;
    try { const res = await showsAPI.update(show.id, { name: trimmed }); setShow(res.data); }
    catch (err) { console.error('Failed to rename show:', err); }
  }
  async function handleStartSave() {
    setEditingStart(false);
    const trimmed = startDraft.trim();
    if (!show?.id) return;
    const iso = hmsToScheduledStart(trimmed);
    if (!iso) return;
    try { const res = await showsAPI.update(show.id, { scheduled_start: iso }); setShow(res.data); }
    catch (err) { console.error('Failed to update show start time:', err); }
  }

  // ── clocks ──
  const now = new Date();
  const timeStr = `${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`;
  let segValue = '--:--', segColor = 'var(--faint)';
  if (onAirCue && onAirStartTime) {
    const remaining = (onAirCue.planned_duration || 0) * 1000 - (Date.now() - onAirStartTime);
    const over = remaining < 0;
    const secs = Math.floor(Math.abs(remaining) / 1000);
    segValue = `${over ? '+' : ''}${pad2(Math.floor(secs / 60))}:${pad2(secs % 60)}`;
    segColor = over ? 'var(--over)' : 'var(--green)';
  }
  const showStartStr = extractHMS(show?.scheduled_start) || '--:--:--';

  const tier2Style = { padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--dim)', background: 'transparent', border: '1px solid var(--border)', cursor: 'pointer', whiteSpace: 'nowrap' };
  const menuItemStyle = { display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' };

  return (
    <div style={{ flexShrink: 0 }}>
      {/* Billing banners (unchanged behaviour; render above the chrome rows) */}
      {subscriptionStatus === 'trial' && trialDaysRemaining !== null && trialDaysRemaining <= 7 && (
        <div className="bg-amber-900/40 border-b border-amber-700 px-4 py-1 flex items-center justify-between">
          <span className="text-amber-300 text-xs">Trial ends in {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}.</span>
          <button onClick={() => navigate('/billing')} className="text-xs text-amber-200 underline hover:text-app-fg ml-4">Subscribe now</button>
        </div>
      )}
      {subscriptionStatus === 'trial_expired' && (
        <div className="bg-red-900/50 border-b border-red-700 px-4 py-1 flex items-center justify-between">
          <span className="text-red-300 text-xs font-medium">Trial expired — vMix and Singular.live are disabled.</span>
          <button onClick={() => navigate('/billing')} className="text-xs text-red-200 underline hover:text-app-fg ml-4">Upgrade</button>
        </div>
      )}
      {subscriptionStatus === 'past_due' && (
        <div className="bg-red-900/50 border-b border-red-700 px-4 py-1 flex items-center justify-between">
          <span className="text-red-300 text-xs font-medium">Payment failed — please update your billing details.</span>
          <button onClick={() => navigate('/billing')} className="text-xs text-red-200 underline hover:text-app-fg ml-4">Update billing</button>
        </div>
      )}

      {/* ── ROW 1 — identity · clocks · status ────────────────────────────── 56px */}
      <div style={{ height: 56, background: 'var(--surface)', borderBottom: '1px solid rgb(var(--c-line-hard))', display: 'flex', alignItems: 'center', gap: 16, padding: '0 20px' }}>
        {/* Left: identity + status + state transition */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <button onClick={onExit} title="Back to dashboard" style={{ color: 'var(--faint)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
            <IconChevronLeft width={18} height={18} />
          </button>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--live)', flexShrink: 0 }} title="MissionControl" />
          {editingName ? (
            <input
              autoFocus value={nameDraft}
              onChange={e => setNameDraft(e.target.value)} onBlur={handleNameSave}
              onKeyDown={e => { if (e.key === 'Enter') handleNameSave(); if (e.key === 'Escape') setEditingName(false); e.stopPropagation(); }}
              style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)', background: 'transparent', border: 'none', borderBottom: '1px solid var(--blue)', outline: 'none', width: 220 }}
            />
          ) : (
            <span
              onClick={canProduce ? () => { setNameDraft(show?.name || ''); setEditingName(true); } : undefined}
              title={canProduce ? 'Click to rename' : undefined} style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)', cursor: canProduce ? 'text' : 'default', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}
            >{show?.name || 'Loading…'}</span>
          )}
          {show?.sport_type && show.sport_type !== 'general' && (
            <span style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--faint)', flexShrink: 0 }}>{show.sport_type}</span>
          )}
          <span className={show?.status === 'live' ? 'on-air-pulse' : undefined} style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em', color: statusInfo.color, border: `1px solid ${statusInfo.color}`, borderRadius: 4, padding: '2px 6px', flexShrink: 0 }}>
            {statusInfo.label}
          </span>
          {isDraft && (
            <button onClick={handleStartShow} style={{ padding: '7px 16px', borderRadius: 7, background: 'var(--golive)', color: '#fff', fontWeight: 500, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>GO LIVE</button>
          )}
          {isLive && (
            <button onClick={handleEndShow} title="End the live show" style={{ padding: '6px 12px', borderRadius: 6, background: 'transparent', color: 'var(--dim)', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', border: '1px solid var(--border)', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>END SHOW</button>
          )}
        </div>

        {/* Centre: clocks */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexShrink: 0 }}>
          {editingStart ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <span style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--dim)', lineHeight: '10px' }}>Show Start</span>
              <input
                autoFocus type="text" value={startDraft} placeholder="HH:MM:SS"
                onChange={e => setStartDraft(e.target.value)} onBlur={handleStartSave}
                onKeyDown={e => { if (e.key === 'Enter') handleStartSave(); if (e.key === 'Escape') setEditingStart(false); e.stopPropagation(); }}
                style={{ width: 90, textAlign: 'right', fontFamily: "'Roboto Mono','Roboto Mono Fallback',monospace", fontSize: 15, color: 'var(--next)', background: 'transparent', border: 'none', borderBottom: '1px solid var(--next)', outline: 'none' }}
              />
            </div>
          ) : (
            <button onClick={canProduce ? () => { setStartDraft(extractHMS(show?.scheduled_start) || ''); setEditingStart(true); } : undefined} title={canProduce ? 'Click to set show start time' : 'Show start'} style={{ background: 'none', border: 'none', padding: 0, cursor: canProduce ? 'pointer' : 'default' }}>
              <ClockStack caption="Show Start" value={showStartStr} valueColor="var(--next)" valuePx={15} />
            </button>
          )}
          <ClockStack caption="Time" value={timeStr} valueColor="rgb(var(--c-fg))" valuePx={22} />
          <ClockStack caption="Seg" value={segValue} valueColor={segColor} valuePx={22} />
        </div>

        {/* Right: connection indicators + hamburger */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, justifyContent: 'flex-end' }}>
          <ConnChip state={syncState} label="SYNC" tooltip="Rundown sync (our server)" />
          {vmixInUse && <ConnChip state={vmixState} label="VMIX" tooltip={vmixTooltip} pulseKey={vmixState === 'connected' ? tallyPulse : 0} />}
          {singularInUse && <ConnChip state={singularState} label="SNGL" tooltip="Singular.live" />}
          <div style={{ position: 'relative', marginLeft: 4 }} ref={menuRef}>
            <button onClick={() => setMenuOpen(v => !v)} title="Menu" style={{ color: 'var(--dim)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
              <IconMenu2 width={20} height={20} />
            </button>
            {menuOpen && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 8, padding: 4, minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,.4)', zIndex: 40 }}>
                <div style={{ padding: '6px 14px 8px', fontSize: 11, color: 'var(--faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', borderBottom: '1px solid var(--border)' }}>{user?.email}</div>
                <button className="mc-menu-item" onClick={handleHelp} style={{ ...menuItemStyle, borderRadius: 6, marginTop: 4 }}>Help</button>
                <button className="mc-menu-item" onClick={handleDownload} style={{ ...menuItemStyle, borderRadius: 6 }} disabled={!show}>Download</button>
                <button className="mc-menu-item" onClick={handlePrint} style={{ ...menuItemStyle, borderRadius: 6 }} disabled={!show}>Print</button>
                {canProduce && (
                  <button className="mc-menu-item" onClick={() => { setMenuOpen(false); onOpenSettings?.(); }} style={{ ...menuItemStyle, borderRadius: 6 }}>Settings</button>
                )}
                <button className="mc-menu-item" onClick={handleResetShow} style={{ ...menuItemStyle, borderRadius: 6 }}>Reset show</button>
                <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
                <button className="mc-menu-item" onClick={() => { setMenuOpen(false); logout(); }} style={{ ...menuItemStyle, borderRadius: 6 }}>Sign out</button>
              </div>
            )}
          </div>
          <EditLockButton />
        </div>
      </div>

      {/* ── ROW 2 — navigation · transport ───────────────────────────────── 44px */}
      <div style={{ height: 44, background: 'var(--surface)', borderBottom: '1px solid rgb(var(--c-line-hard))', display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px' }}>
        {/* Left: view tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
          {VIEWS.map(v => {
            const on = activeView === v.id;
            return (
              <button
                key={v.id}
                onClick={() => onViewChange(v.id)}
                style={{
                  display: 'inline-flex', alignItems: 'baseline', gap: 6, padding: '5px 11px', borderRadius: 6,
                  fontSize: 14, fontWeight: 500, color: on ? 'var(--text)' : 'var(--dim)',
                  background: on ? 'var(--surface3)' : 'transparent', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {v.label}
                <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '.06em', color: 'var(--faint)', fontFamily: "'Roboto Mono','Roboto Mono Fallback',monospace" }}>{v.key}</span>
              </button>
            );
          })}
        </div>

        {/* Right: transport — tier2 (BACK/SKIP), tier1 (TAKE), tier3 (mode chips) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {/* data-lock-allow: the transport keeps working while the edit lock is on */}
          <button data-lock-allow onClick={onBack} title="Back (B) — return to the previous cue" style={tier2Style}>BACK</button>
          <button data-lock-allow onClick={onSkip} title="Skip (S) — skip the next cue without airing it" style={tier2Style}>SKIP</button>
          <button
            data-lock-allow
            onClick={onTake}
            disabled={!canTake}
            title="Take (Space)"
            style={{
              padding: '9px 22px', borderRadius: 8, fontSize: 15, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.08em',
              background: canTake ? 'var(--live)' : 'var(--surface2)', color: canTake ? '#fff' : 'var(--faint)',
              border: canTake ? '1px solid var(--live)' : '1px solid var(--border)',
              boxShadow: canTake ? '0 1px 10px color-mix(in srgb, var(--live) 28%, transparent)' : 'none',
              cursor: canTake ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap',
            }}
          >TAKE</button>
          <div style={{ display: 'flex', gap: 4, marginLeft: 4 }}>
            <ModeChip
              active={autoMode}
              tone="--green"
              onClick={onToggleAutoMode}
              title={isLive
                ? 'Auto: auto-advance cues when their in-time is reached'
                : 'Auto mode needs the show to be live'}
            >AUTO</ModeChip>
            <ModeChip active={takeGuard} tone="--next" onClick={onToggleTakeGuard} title="Guard: ignore a second TAKE within 0.5s to prevent accidental double-takes">GUARD</ModeChip>
            <ModeChip active={followOnAir} tone="--blue" onClick={toggleFollowOnAir} title="Follow: auto-scroll the rundown to the on-air cue">FOLLOW</ModeChip>
          </div>
        </div>
      </div>
    </div>
  );
}
